import type { ApiErrorBody } from '@fleetcore/shared'

import {
  getAccessToken,
  getRefreshToken,
  notifySessionEnded,
  setTokens,
} from '../auth/token-storage'
import { env } from '../env'
import { ApiError } from './api-error'
import { endpoints } from './endpoints'

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  /** Serialized as JSON on every attempt, so a retry does not reuse a spent body. */
  json?: unknown
  /** Auth endpoints opt out: a 401 from /auth/login is an answer, not a stale token. */
  skipAuthRefresh?: boolean
}

export interface TokenPair {
  accessToken: string
  refreshToken: string
}

function buildRequest(init: ApiRequestInit, token: string | null): RequestInit {
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.json !== undefined) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  // No tenant header, no tenant query parameter, ever. Tenant scope is the `tid`
  // claim inside the signed access token and is resolved server-side; anything
  // the client could set here would be a cross-tenant vector by construction.
  const request: RequestInit = { ...init, headers }
  if (init.json !== undefined) request.body = JSON.stringify(init.json)
  delete (request as ApiRequestInit).json
  delete (request as ApiRequestInit).skipAuthRefresh
  return request
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T
  }

  const isJson = response.headers.get('Content-Type')?.includes('application/json') ?? false
  const payload: unknown = isJson ? await response.json().catch(() => null) : null

  if (response.ok) return payload as T

  const body = (payload ?? {}) as Partial<ApiErrorBody>
  throw new ApiError(
    response.status,
    body.code ?? `http_${response.status}`,
    body.message ?? response.statusText,
    body,
  )
}

async function send(path: string, init: ApiRequestInit, token: string | null): Promise<Response> {
  try {
    return await fetch(`${env.apiUrl}${path}`, buildRequest(init, token))
  } catch (cause) {
    // fetch only rejects on transport failure; every HTTP status resolves.
    throw ApiError.network(cause)
  }
}

/**
 * One refresh at a time, shared by every caller.
 *
 * Mounting the dashboard fires half a dozen queries at once. If each of their
 * 401s started its own refresh, the first rotation would invalidate the refresh
 * token the others are still holding, the server would see a superseded
 * generation, and — correctly — treat it as a replay and revoke the entire
 * family. The user would be signed out for the crime of loading a page. So the
 * first caller creates the promise, everyone else awaits the same one, and it is
 * cleared only once settled so a later 401 can start a fresh attempt.
 */
let refreshInFlight: Promise<string | null> | null = null

async function requestRotatedTokens(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null

  try {
    const response = await fetch(`${env.apiUrl}${endpoints.refresh}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // Sent for the deployment variant where the API keeps the refresh token in
      // an httpOnly cookie; harmless when it travels in the body instead.
      credentials: 'include',
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) return null

    const tokens = (await response.json()) as Partial<TokenPair>
    if (typeof tokens.accessToken !== 'string') return null

    setTokens({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken ?? null })
    return tokens.accessToken
  } catch {
    // A network blip must not look like a revoked session, so the caller decides.
    return null
  }
}

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = requestRotatedTokens().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

/** Exposed for tests that need to observe the shared-promise behaviour. */
export function isRefreshInFlight(): boolean {
  return refreshInFlight !== null
}

export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const response = await send(path, init, getAccessToken())

  if (response.status !== 401 || init.skipAuthRefresh) return parseResponse<T>(response)

  const renewed = await refreshAccessToken()
  if (!renewed) {
    notifySessionEnded('expired')
    throw new ApiError(401, 'session_expired', 'Refresh token rejected or absent')
  }

  return parseResponse<T>(await send(path, init, renewed))
}

export const api = {
  get: <T>(path: string, init?: ApiRequestInit) => apiFetch<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, json?: unknown, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'POST', json }),
  patch: <T>(path: string, json?: unknown, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'PATCH', json }),
  delete: <T>(path: string, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'DELETE' }),
}
